import type { Cents } from "../domain/money";
import type { QuantityUnit } from "../domain/feature";

/**
 * Home Depot response mapping (Phase 9B.1) — PURE functions over the
 * provider's JSON. No I/O, no secrets, no React: the transport lives in the
 * platform shell (Rust today, a server endpoint under SaaS) and everything
 * interesting happens here where it can be tested against fixtures without
 * spending API quota.
 *
 * Nothing is invented: a field the provider did not return stays null, and
 * the UI says so rather than guessing.
 */

export interface HomeDepotSearchResult {
  /** Home Depot's own product identifier (external identity). */
  productId: string;
  title: string;
  brand: string | null;
  modelNumber: string | null;
  priceCents: Cents | null;
  /** Struck-through "was" price when the provider reported one. */
  priceWasCents: Cents | null;
  rating: number | null;
  reviews: number | null;
  thumbnailUrl: string | null;
  /** Canonical Home Depot product page (provider-supplied when present). */
  link: string | null;
  freeDelivery: boolean | null;
  /** Live store stock the provider reported for the searched store. */
  pickupStoreName: string | null;
  pickupQuantity: number | null;
  collection: string | null;
}

/**
 * Home Depot titles omit the brand ("12 in. Rough In 2-Piece … Toilet"),
 * so a catalog name needs both. Never duplicates a brand already present.
 */
export function displayProductName(input: { brand?: string | null; title: string }): string {
  const brand = (input.brand ?? "").trim();
  const title = input.title.trim();
  if (brand === "" || title.toLowerCase().startsWith(brand.toLowerCase())) return title;
  return `${brand} ${title}`;
}

export interface HomeDepotSpecification {
  group: string;
  name: string;
  value: string;
}

export interface HomeDepotProductDetail {
  productId: string;
  title: string;
  brand: string | null;
  modelNumber: string | null;
  storeSku: string | null;
  upc: string | null;
  priceCents: Cents | null;
  link: string | null;
  imageUrl: string | null;
  specifications: HomeDepotSpecification[];
  highlights: string[];
  /** Human-readable fulfillment/availability lines, verbatim. */
  fulfillment: string[];
  /** Structured package data the provider publishes for sold-by-area
   *  goods: how much ONE package contains, and the unit it is sold in
   *  ("sq. ft."). Authoritative — no parsing of marketing text. */
  packageQuantity: number | null;
  unitLabel: string | null;
  pricePerUnitCents: Cents | null;
}

/* ------------------------------------------------------------------ */
/* Money / primitives                                                  */
/* ------------------------------------------------------------------ */

/** Provider prices arrive as numbers (65.94) or strings ("$65.94"). */
export function parseProviderPriceCents(value: unknown): Cents | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value * 100);
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.]/g, "");
    if (cleaned === "" || Number.isNaN(Number(cleaned))) return null;
    return Math.round(Number(cleaned) * 100);
  }
  if (value && typeof value === "object") {
    // Some payloads nest the amount, e.g. { price: { value: 65.94 } }.
    const record = value as Record<string, unknown>;
    const nested = record.value ?? record.amount;
    if (nested !== undefined) return parseProviderPriceCents(nested);
  }
  return null;
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** thumbnails is an array of arrays of URLs; take the first usable one. */
function firstThumbnail(value: unknown): string | null {
  if (typeof value === "string") return str(value);
  if (!Array.isArray(value)) return null;
  for (const entry of value) {
    const found = firstThumbnail(entry);
    if (found) return found;
  }
  return null;
}

function brandName(value: unknown): string | null {
  if (typeof value === "string") return str(value);
  if (value && typeof value === "object") return str((value as Record<string, unknown>).name);
  return null;
}

/* ------------------------------------------------------------------ */
/* Search (engine=home_depot)                                          */
/* ------------------------------------------------------------------ */

/** The store the provider actually priced against (honesty about market). */
export function parseSearchStoreContext(json: unknown): { storeId: string | null; storeName: string | null } {
  const root = (json ?? {}) as Record<string, unknown>;
  const info = (root.search_information ?? {}) as Record<string, unknown>;
  const params = (root.search_parameters ?? {}) as Record<string, unknown>;
  return {
    storeId: str(info.store_id) ?? str(params.store_id),
    storeName: str(info.store_name),
  };
}

export function parseSearchResponse(json: unknown): HomeDepotSearchResult[] {
  const root = (json ?? {}) as Record<string, unknown>;
  const products = Array.isArray(root.products) ? root.products : [];
  const results: HomeDepotSearchResult[] = [];
  for (const raw of products) {
    if (!raw || typeof raw !== "object") continue;
    const p = raw as Record<string, unknown>;
    const productId = str(p.product_id);
    const title = str(p.title);
    if (!productId || !title) continue; // identity + name are non-negotiable
    const delivery = (p.delivery ?? {}) as Record<string, unknown>;
    const pickup = (p.pickup ?? {}) as Record<string, unknown>;
    results.push({
      productId,
      title,
      brand: brandName(p.brand),
      modelNumber: str(p.model_number),
      priceCents: parseProviderPriceCents(p.price),
      priceWasCents: parseProviderPriceCents(p.price_was),
      rating: num(p.rating),
      reviews: num(p.reviews),
      thumbnailUrl: firstThumbnail(p.thumbnails),
      link: str(p.link),
      freeDelivery: typeof delivery.free === "boolean" ? delivery.free : null,
      pickupStoreName: str(pickup.store_name),
      pickupQuantity: num(pickup.quantity),
      collection: str(p.collection),
    });
  }
  return results;
}

/* ------------------------------------------------------------------ */
/* Detail (engine=home_depot_product)                                  */
/* ------------------------------------------------------------------ */

function parseSpecifications(value: unknown): HomeDepotSpecification[] {
  const out: HomeDepotSpecification[] = [];
  const visit = (node: unknown, group: string) => {
    if (Array.isArray(node)) {
      for (const item of node) visit(item, group);
      return;
    }
    if (!node || typeof node !== "object") return;
    const o = node as Record<string, unknown>;
    // Grouped shape: { title: "Details", specifications: [...] }
    // Grouped shapes seen in the wild:
    //   { key: "Details", value: [{name, value}, …] }   <- current
    //   { title: "Details", specifications: [...] }
    const nested = o.specifications ?? o.values ?? o.items ?? (Array.isArray(o.value) ? o.value : null);
    if (Array.isArray(nested)) {
      visit(nested, str(o.key) ?? str(o.title) ?? str(o.name) ?? group);
      return;
    }
    const name = str(o.name) ?? str(o.key) ?? str(o.title);
    const val = str(o.value) ?? str(o.text);
    if (name && val) out.push({ group, name, value: val });
  };
  visit(value, "");
  return out;
}

function parseFulfillment(value: unknown): string[] {
  if (!value || typeof value !== "object") return [];
  const f = value as Record<string, unknown>;
  const lines: string[] = [];
  const options = Array.isArray(f.options) ? f.options : [];
  for (const raw of options) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const parts = [str(o.title) ?? str(o.type), str(o.arrival_time), str(o.bottom)].filter(Boolean);
    if (parts.length > 0) lines.push(parts.join(" — "));
  }
  const store = f.store && typeof f.store === "object" ? (f.store as Record<string, unknown>) : null;
  const qty = num(f.quantity) ?? num(store?.quantity);
  if (qty !== null) lines.push(`Quantity available: ${qty}`);
  return lines;
}

function firstImage(value: unknown): string | null {
  if (!Array.isArray(value)) return firstThumbnail(value);
  for (const entry of value) {
    if (typeof entry === "string") return str(entry);
    if (entry && typeof entry === "object") {
      const o = entry as Record<string, unknown>;
      const found = str(o.link) ?? str(o.url) ?? firstThumbnail(o.thumbnails);
      if (found) return found;
    }
  }
  return null;
}

export function parseProductResponse(json: unknown): HomeDepotProductDetail | null {
  const root = (json ?? {}) as Record<string, unknown>;
  const raw = (root.product_results ?? root.product ?? null) as Record<string, unknown> | null;
  if (!raw || typeof raw !== "object") return null;
  const productId = str(raw.product_id);
  const title = str(raw.title);
  if (!productId || !title) return null;
  return {
    productId,
    title,
    brand: brandName(raw.brand),
    modelNumber: str(raw.model_number),
    storeSku: str(raw.store_sku_number) ?? str(raw.sku),
    upc: str(raw.upc),
    priceCents: parseProviderPriceCents(raw.price),
    link: str(raw.link),
    imageUrl: firstImage(raw.images),
    specifications: parseSpecifications(raw.specifications),
    highlights: Array.isArray(raw.highlights)
      ? raw.highlights.map(str).filter((x): x is string => x !== null)
      : [],
    fulfillment: parseFulfillment(raw.fulfillment),
    packageQuantity: num(raw.package_quantity),
    unitLabel: str(raw.unit),
    pricePerUnitCents: parseProviderPriceCents(raw.price_per_unit),
  };
}

/* ------------------------------------------------------------------ */
/* Coverage / package metadata                                         */
/* ------------------------------------------------------------------ */

export interface CoverageFinding {
  /** Construction units covered by ONE purchase unit, x100. */
  coveragePerUnitX100: number;
  coverageUnit: QuantityUnit;
  purchaseUnit: QuantityUnit;
  /** The specification that produced it (provenance, shown to the user). */
  sourceLabel: string;
}

const COVERAGE_PATTERNS: Array<{ re: RegExp; unit: QuantityUnit; purchaseUnit: QuantityUnit }> = [
  { re: /coverage\s*area\s*\(?\s*sq\.?\s*\.?\s*ft\.?\)?/i, unit: "sqft", purchaseUnit: "case" },
  { re: /square\s*footage\s*(per\s*(case|box|carton))?/i, unit: "sqft", purchaseUnit: "case" },
  { re: /sq\.?\s*\.?\s*ft\.?\s*per\s*(case|box|carton)/i, unit: "sqft", purchaseUnit: "case" },
  { re: /coverage\s*(area)?\s*per\s*(case|box|carton)/i, unit: "sqft", purchaseUnit: "case" },
  { re: /linear\s*f(ee)?t\.?\s*per\s*(case|box|carton)/i, unit: "lf", purchaseUnit: "case" },
];

/**
 * Read package coverage from the detail specifications when the provider
 * supplies it (flooring commonly does). Returns null when it does not —
 * the app then asks for ONLY that one field rather than the whole product.
 */
/** Map the provider's selling unit onto our construction units. */
export function coverageUnitFromLabel(label: string | null): QuantityUnit | null {
  const l = (label ?? "").trim().toLowerCase().replace(/\./g, "").replace(/\s+/g, " ");
  if (l === "sq ft" || l === "sqft" || l === "square feet" || l === "square foot") return "sqft";
  if (l === "lin ft" || l === "linear ft" || l === "linear feet" || l === "lf") return "lf";
  if (l === "each" || l === "ea") return "each";
  return null;
}

/**
 * Package coverage from the provider's STRUCTURED fields
 * (`package_quantity` + `unit`) — the authoritative source for goods sold
 * by area. Returns null when the provider did not publish them.
 */
export function coverageFromPackageFields(detail: {
  packageQuantity: number | null;
  unitLabel: string | null;
}): CoverageFinding | null {
  const unit = coverageUnitFromLabel(detail.unitLabel);
  const qty = detail.packageQuantity;
  if (unit === null || qty === null || qty <= 0) return null;
  if (unit === "each") return null; // per-item goods need no package figure
  return {
    coveragePerUnitX100: Math.round(qty * 100),
    coverageUnit: unit,
    purchaseUnit: "case",
    sourceLabel: `Home Depot package data: ${qty} ${detail.unitLabel} per case`,
  };
}

export function coverageFromSpecifications(specs: HomeDepotSpecification[]): CoverageFinding | null {
  for (const spec of specs) {
    for (const pattern of COVERAGE_PATTERNS) {
      if (!pattern.re.test(spec.name)) continue;
      const match = spec.value.replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
      if (!match) continue;
      const value = Number(match[1]);
      if (!Number.isFinite(value) || value <= 0) continue;
      return {
        coveragePerUnitX100: Math.round(value * 100),
        coverageUnit: pattern.unit,
        purchaseUnit: pattern.purchaseUnit,
        sourceLabel: `${spec.name}: ${spec.value}`,
      };
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Query generation + outbound URL                                     */
/* ------------------------------------------------------------------ */

/** Attribute keys that meaningfully narrow a Home Depot search. */
const USEFUL_ATTRIBUTE_KEYS = [
  "type", "style", "size", "color", "finish", "width", "length",
  "rough-in", "rough in", "shape", "material", "height",
];

/**
 * Deterministic default query from the material definition plus any known
 * structured attributes. No AI, no guessing beyond what the domain states.
 */
export function buildSearchQuery(input: {
  materialName: string;
  componentName?: string | null;
  attributes?: Array<{ key: string; value: string }>;
}): string {
  const base = (input.materialName || input.componentName || "").trim();
  const extras: string[] = [];
  for (const attr of input.attributes ?? []) {
    const key = attr.key.trim().toLowerCase();
    if (!USEFUL_ATTRIBUTE_KEYS.includes(key)) continue;
    const value = attr.value.trim();
    if (value === "" || base.toLowerCase().includes(value.toLowerCase())) continue;
    extras.push(value);
  }
  return [base, ...extras].join(" ").replace(/\s+/g, " ").trim();
}

/**
 * The outbound product page. Prefer the provider's own canonical link;
 * otherwise build Home Depot's stable product path from the product id.
 * Never fabricates a marketing slug.
 */
export function homeDepotProductUrl(input: { link?: string | null; productId?: string | null }): string | null {
  const link = normalizeHomeDepotHost((input.link ?? "").trim());
  if (/^https?:\/\//i.test(link)) return link;
  const id = (input.productId ?? "").trim();
  return id === "" ? null : `https://www.homedepot.com/p/${encodeURIComponent(id)}`;
}

/**
 * The provider returns product links on its own proxy host
 * (apionline.homedepot.com). Customers must land on the real storefront,
 * so the host is normalized while the product path is preserved exactly.
 */
export function normalizeHomeDepotHost(url: string): string {
  if (url === "") return url;
  return url.replace(/^https?:\/\/(?:apionline|api)\.homedepot\.com/i, "https://www.homedepot.com");
}

/** Stable cache key for a provider request (engine + sorted parameters). */
export function providerCacheKey(engine: string, params: Record<string, string>): string {
  const entries = Object.entries(params)
    .filter(([k]) => k !== "api_key")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`);
  return `${engine}?${entries.join("&")}`;
}
